import { max, min, parse } from 'date-fns';
import { z } from 'zod';

import { parseCsvString } from '@/utils/csv-parser';
import { type Metric, type MetricsGroup } from '@/warranty-v3/entities/metric';

import { GetMetricsQuery } from '../get-metrics';

import metricsTimeseries from './data/metrics-timeseries.csv?raw';

/**
 * Maps alertType to the corresponding description_id filter
 */
function getDescriptionIdFilter(alertType?: string): string[] | undefined {
  if (!alertType) return undefined;

  switch (alertType) {
    case 'warranty_tag':
      return ['Availability'];
    case 'warranty_tag_pcs':
      return ['Availability PCS'];
    case 'warranty_rte':
      return ['RTE'];
    case 'warranty_soh':
      return ['SOH'];
    default:
      return undefined;
  }
}

export const loadData = parseCsvString(
  metricsTimeseries,
  z.object({
    Column1: z.coerce.string(),
    site_id: z.coerce.string(),
    accure_id: z.coerce.string(),
    window_end_date: z.coerce.string(),
    description_id: z.enum([
      'Availability',
      'Performance',
      'Reliability',
      'RTE',
      'SOH',
    ]),
    analyzed_at_time: z.coerce.string(),
    value: z.coerce.number(),
    payment: z.coerce.number(),
    threshold: z.coerce.number(),
    total_penalty: z.coerce.number(),
  }),
);

function getDataTimeRange(
  data: Awaited<typeof loadData>,
  systemId: string,
  alertType?: string,
): {
  start: Date;
  end: Date;
} {
  // Filter data for the specific site
  let filteredData = data.filter((d) => d.accure_id === systemId);

  // Apply alertType filter if provided
  const descriptionIdFilter = getDescriptionIdFilter(alertType);
  if (descriptionIdFilter) {
    filteredData = filteredData.filter((d) =>
      descriptionIdFilter.includes(d.description_id),
    );
  }

  if (filteredData.length === 0) {
    // Return fallback dates if no data found for the systemId
    const now = new Date();
    return { start: now, end: now };
  }

  const dates = filteredData.map((d) =>
    parse(d.window_end_date, 'dd.MM.yy', new Date()),
  );

  const start = min(dates);
  const end = max(dates);

  return { start, end };
}

function generateGroupsFromData(
  data: Awaited<typeof loadData>,
  systemId: string,
  alertType?: string,
): MetricsGroup[] {
  // Filter data for the specific site
  let filteredData = data.filter((d) => d.accure_id === systemId);

  // Apply alertType filter if provided
  const descriptionIdFilter = getDescriptionIdFilter(alertType);
  if (descriptionIdFilter) {
    filteredData = filteredData.filter((d) =>
      descriptionIdFilter.includes(d.description_id),
    );
  }

  if (filteredData.length === 0) {
    return [];
  }

  // Group by description_id
  const groupedByMetric = filteredData.reduce(
    (acc, item) => {
      if (!acc[item.description_id]) {
        acc[item.description_id] = [];
      }
      acc[item.description_id].push(item);
      return acc;
    },
    {} as Record<string, typeof filteredData>,
  );

  // Convert to MetricsGroup array
  const groups: MetricsGroup[] = Object.entries(groupedByMetric).map(
    ([descriptionId, metricData], index) => {
      // Sort by date to ensure proper time series order
      const sortedData = metricData.sort((a, b) => {
        const dateA = parse(a.window_end_date, 'dd.MM.yy', new Date());
        const dateB = parse(b.window_end_date, 'dd.MM.yy', new Date());
        return dateA.getTime() - dateB.getTime();
      });

      // Calculate aggregated values
      const latestData = sortedData[sortedData.length - 1];
      const avgValue =
        sortedData.reduce((sum, d) => sum + d.value, 0) / sortedData.length;

      // Generate value series (value over time)
      const valueSeries = {
        points: sortedData.map(
          (d) =>
            [
              parse(d.window_end_date, 'dd.MM.yy', new Date()),
              d.value / 100, // Convert percentage to decimal
            ] as [Date, number],
        ),
      };

      // Generate caped LD series (payment/penalty over time)
      const capedLDSeries = {
        points: sortedData.map(
          (d) =>
            [parse(d.window_end_date, 'dd.MM.yy', new Date()), d.payment] as [
              Date,
              number,
            ],
        ),
      };

      const metric: Metric = {
        id: descriptionId.toLowerCase(),
        name: descriptionId,
        order: 0,
        unit: 'percent',
        value: avgValue / 100, // Convert percentage to decimal
        guaranteedValue: {
          base: latestData.threshold / 100, // Convert percentage to decimal
          penalty: latestData.total_penalty,
          effective: (latestData.threshold - latestData.total_penalty) / 100,
        },
        capedLD: latestData.payment || undefined,
        valueSeries,
        capedLDSeries,
      };

      // Get group-level capedLD (final cumulative value, since payments are cumulative)
      const groupCapedLD = sortedData[sortedData.length - 1].payment;

      return {
        id: descriptionId.toLowerCase(),
        name: descriptionId,
        order: index,
        metrics: [metric],
        capedLD: groupCapedLD || undefined,
      };
    },
  );

  return groups;
}

export async function metrics(
  input: GetMetricsQuery.Input,
): Promise<GetMetricsQuery.Output> {
  const data = await loadData;

  const { start, end } = getDataTimeRange(
    data,
    input.systemId,
    input.alertType,
  );
  const groups = generateGroupsFromData(data, input.systemId, input.alertType);

  return {
    start,
    end,
    currency: 'USD',
    groups,
    isDataAvailable: true,
  };
}
